import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CompleteDDLData,
  type CancelConsultMetadataResponse,
  type statusRequestService,
  type CompleteConsultParams,
  type CompleteConsult,
  type OnHoldConsultMetadataResponse,
  type OnHoldReasonParams,
} from './statusRequestService';
import { type CancelReasonFacilitySubmitParams } from './CancelConsultModel';

type StatusRequestService = ReturnType<typeof statusRequestService>;

export const statusRequestHooks = (
  statusRequestService: StatusRequestService
) => {
  const useCancelConsultMetadata = (portalType: string) => {
    const { data, isLoading, isError, error, refetch } =
      useQuery<CancelConsultMetadataResponse>({
        queryKey: ['CancelConsultMetadata'],
        queryFn: async () => {
          const data =
            await statusRequestService.getCancelConsultMetadata(portalType);
          return data;
        },

        staleTime: 0,
        gcTime: 0,
      });

    return {
      CancelConsultMetadata: data,
      isLoading,
      isError,
      error,
      refetch,
    };
  };
  const useSaveCancelConsult = () => {
    const queryClient = useQueryClient();
    return useMutation<
      CancelReasonFacilitySubmitParams,
      Error,
      CancelReasonFacilitySubmitParams & {
        userId: string;
        portalType: string;
        consultId: number;
      }
    >({
      mutationFn: async ({ userId, portalType, consultId, ...payload }) => {
        const data = await statusRequestService.saveCancelConsult(
          userId,
          portalType,
          consultId,
          payload
        );
        return data;
      },

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['ConsultsListResponse'],
        });
      },
      onError: (error) => {
        console.error('Error saving consult cancel reason:', error);
      },
    });
  };

  const useCompleteDDLDetails = (portalType: string, consultId: string) => {
    const { data, isLoading, isError, error } = useQuery<CompleteDDLData>({
      queryKey: ['CompleteDDLDetails'],
      queryFn: async () => {
        const data = await statusRequestService.getCompleteDropdownData(
          portalType,
          consultId
        );
        return data;
      },

      staleTime: 0,
      gcTime: 0,
    });

    return {
      completeDDLDetails: data,
      isLoading,
      isError,
      error,
    };
  };

  const useCompleteConsult = (portalType: string) => {
    return useMutation<CompleteConsult, unknown, CompleteConsultParams>({
      mutationFn: async (params) => {
        const data = await statusRequestService.submitCompleteForm(
          params.userId,
          params.consultId,
          {
            billable: params.billable,
            diagnosis: params.diagnosis.map((d) => d.id),
            recommendation: params.recommendation,
            emCodes: params.emCodes,
            isSurge: params.isSurge,
            opsReasonForCompletion: params.opsReasonForCompletion,
            unitOrDepartment: params.unitOrDepartment,
            followUp: params.followUp,
          },
          portalType
        );
        return data;
      },

      onSuccess: (data) => {
        console.log('Complete a Consult successfully:', data);
      },
    });
  };

  const useOnHoldConsultMetadata = (portalType: string) => {
    const { data, isLoading, isError, error, refetch } =
      useQuery<OnHoldConsultMetadataResponse>({
        queryKey: ['OnHoldConsultMetadata'],
        queryFn: async () => {
          const data =
            await statusRequestService.getOnHoldConsultMetadata(portalType);
          return data;
        },

        staleTime: 0,
        gcTime: 0,
      });

    return {
      OnHoldConsultMetadata: data,
      isLoading,
      isError,
      error,
      refetch,
    };
  };

  const useSaveOnHoldConsult = () => {
    const queryClient = useQueryClient();
    return useMutation<
      OnHoldReasonParams,
      Error,
      OnHoldReasonParams & {
        userId: string;
        portalType: string;
      }
    >({
      mutationFn: async ({ userId, portalType, ...payload }) => {
        const data = await statusRequestService.saveOnHoldConsult(
          userId,
          portalType,
          payload
        );
        return data;
      },

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['ConsultsListResponse'],
        });
      },
      onError: (error) => {
        console.error('Error saving consult cancel reason:', error);
      },
    });
  };

  return {
    useCancelConsultMetadata,
    useSaveCancelConsult,
    useCompleteDDLDetails,
    useCompleteConsult,
    useOnHoldConsultMetadata,
    useSaveOnHoldConsult,
  };
};
